/**
 * Les fournisseurs (V1.6-11).
 *
 * Une liste, rien de plus. Ce n'est pas un CRM : HD-Vault ne suit ni les
 * opportunités, ni les relances, ni les contacts multiples. Cette page existe
 * pour atteindre une fiche, et la fiche existe pour lire ce qu'on a vécu avec
 * ce fournisseur.
 */
import { Link, useSearchParams } from "react-router-dom";
import { Truck } from "lucide-react";

import { useSuppliers } from "@/api/queries";
import type { Supplier } from "@/api/types";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Input } from "@/components/Input";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";

const PAGE_SIZE = 50;

export function SuppliersPage() {
  const [params, setParams] = useSearchParams();
  const offset = Number(params.get("offset") ?? 0);
  const search = params.get("search") ?? "";
  const includeInactive = params.get("inactive") === "1";

  const { data, isLoading, error, refetch } = useSuppliers({
    limit: PAGE_SIZE,
    offset,
    include_inactive: includeInactive,
    ...(search ? { search } : {}),
  } as Parameters<typeof useSuppliers>[0]);

  function update(key: string, value: string | null) {
    const merged = new URLSearchParams(params);
    merged.delete("offset");
    if (value) merged.set(key, value);
    else merged.delete(key);
    setParams(merged, { replace: true });
  }

  return (
    <Page>
      <PageHeader
        title="Fournisseurs"
        subtitle="Chez qui on achète. La fiche dit ce qu'on a réellement vécu avec eux."
        actions={
          <Link
            to="/suppliers/comparison"
            className="inline-flex h-9 items-center rounded-md border border-border px-3 text-[13px] text-ink hover:bg-surface-hover"
          >
            Comparer
          </Link>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          className="max-w-xs"
          placeholder="Chercher un fournisseur…"
          defaultValue={search}
          onChange={(event) => update("search", event.target.value || null)}
        />
        <label className="flex items-center gap-1.5 text-[12px] text-ink-muted">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(event) => update("inactive", event.target.checked ? "1" : null)}
          />
          Inclure les fournisseurs retirés
        </label>
      </div>

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={4} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={<Truck className="size-6" aria-hidden />}
              title="Aucun fournisseur"
              description="Les fournisseurs se créent depuis les achats, quand on passe une première commande."
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="max-w-0">Nom</Th>
                      <Th className="hidden @3xl:table-cell">Contact</Th>
                      <Th className="hidden @4xl:table-cell">Compte</Th>
                      <Th>État</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((supplier) => (
                      <SupplierRow key={supplier.id} supplier={supplier} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((supplier) => (
                  <li key={supplier.id}>
                    <Link to={`/suppliers/${supplier.id}`} className="block px-3 py-3">
                      <p className="text-[14px] font-medium text-ink">{supplier.name}</p>
                      <p className="text-[12px] text-ink-subtle">
                        {supplier.contact_name ?? "Aucun contact"}
                        {supplier.active ? "" : " · retiré"}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={data.total}
            onChange={(next) => update("offset", next === 0 ? null : String(next))}
          />
        )}
      </div>
    </Page>
  );
}

function SupplierRow({ supplier }: { supplier: Supplier }) {
  return (
    <Tr>
      <Td className="max-w-0">
        <Link
          to={`/suppliers/${supplier.id}`}
          className="block truncate font-medium text-ink hover:text-brand"
        >
          {supplier.name}
        </Link>
      </Td>
      <Td className="hidden text-[13px] text-ink-muted @3xl:table-cell">
        {supplier.contact_name ?? "—"}
      </Td>
      <Td className="hidden font-mono text-[12px] text-ink-subtle @4xl:table-cell">
        {supplier.account_number ?? "—"}
      </Td>
      <Td>
        {supplier.active ? (
          <StatusBadge tone="available">Actif</StatusBadge>
        ) : (
          <StatusBadge tone="out">Retiré</StatusBadge>
        )}
      </Td>
    </Tr>
  );
}
